import { FormlyFormOptions } from '@ngx-formly/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute } from '@angular/router';

import { SnCurrentOrganization } from '@lib/auth';
import { ISnHasForm, SnFormFieldConfig } from '@lib/form';

@Component({
  selector: 'sn-slide-show-dialog',
  templateUrl: './plate-form-dialog.component.html',
  styleUrls: ['./plate-form-dialog.component.scss'],
})
export class SnPlateFormDialogComponent implements ISnHasForm, OnInit, AfterViewInit, OnDestroy {
  get cameraNameOptions$(): Observable<string[]> {
    return this._cameraNameOptions$.asObservable();
  }

  get getStartControl(): AbstractControl {
    return this.form.get('start');
  }

  get getEndControl(): AbstractControl {
    return this.form.get('end');
  }

  get vigilantEnabled(): boolean {
    return this._currentOrganization.enabledIntegrations.includes('vigilant_ps');
  }

  get flockEnabled(): boolean {
    return this._currentOrganization.enabledIntegrations.includes('flock');
  }

  get eagleEyeEnabled(): boolean {
    return this._currentOrganization.enabledIntegrations.includes('eagleeyenetworks');
  }

  public isLoading = false;
  public readonly form = new UntypedFormGroup({});
  public formModel: any = {};
  public readonly formOptions: FormlyFormOptions = {};

  public formFields: SnFormFieldConfig[];
  public basicFormFields: SnFormFieldConfig[];
  public vehicleColorOptions: any[] = [];
  public vehicleMakeOptions: any[] = [];
  public vehicleModelOptions: any[] = [];
  public vehicleBodyTypeOptions: any[] = [];

  @ViewChild('tabs')
  public tabs: MatTabGroup;

  private _destroy$ = new Subject();
  private _cameraNameOptions$ = new BehaviorSubject<string[]>([]);
  private _dynamicFields: SnFormFieldConfig[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      fields: any;
      formModel: any;
      history?: boolean;
    },
    private readonly _dialogRef: MatDialogRef<SnPlateFormDialogComponent>,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _cd: ChangeDetectorRef,
    private readonly _currentOrganization: SnCurrentOrganization,
  ) {
    this._genFormFields();
  }

  public ngOnInit(): void {}

  public ngAfterViewInit(): void {
    this._parseQueryParams();
    this._patchForm();
    this._cd.detectChanges();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public cancel(): void {
    this._dialogRef.close();
  }

  public clearFilters(): void {
    this.formOptions.resetModel();
    this.formModel = {};
  }

  public submitForm(): void {
    const params: any = {
      ...this.formModel,
    };

    if (this.formModel.start) {
      params.event_timestamp__gte = Math.floor(new Date(this.formModel.start).getTime() / 1000);
    }

    if (this.formModel.end) {
      params.event_timestamp__lte = Math.ceil(new Date(this.formModel.end).getTime() / 1000);
    }

    this._genDyanmicParams(params);
  }

  private _patchForm(): void {
    if (!this.data.formModel) {
      return;
    }
    this.formModel = {
      ...this.formModel,
      ...this.data.formModel,
    };
    this._cd.detectChanges();
  }

  private _advancedToBasic(params: any): any {
    const data = { ...params };
    data.plateSearch = params.plate || this.data?.formModel?.plate || '';
    if (!this.data.history) {
      if (params.plate__startswith) {
        data.plateSearch = `${params.plate__startswith}*`;
      }
      if (params.plate__endswith) {
        data.plateSearch = `*${params.plate__endswith}`;
      }
      if (params.plate__contains__in) {
        data.plateSearch = `*${params.plate__contains__in}*`;
      }
    }

    data.timeOption = 'days';
    const today = new Date();
    const start = new Date(params.start);
    const msInDay = 24 * 60 * 60 * 1000;

    start.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    data.numberOf = Math.ceil((+today - +start) / msInDay) || 1;
    return data;
  }

  private _parseQueryParams(): void {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const params = { ...this._activatedRoute.snapshot.queryParams };
    this.formModel.mode = params.mode || 'Basic';
    const start = params.event_timestamp__gte
      ? new Date(params.event_timestamp__gte * 1000)
      : yesterday;
    const end = params.event_timestamp__lte
      ? new Date(params.event_timestamp__lte * 1000)
      : tomorrow;
    this.formModel.start = start;
    this.formModel.end = end;
    if (params.include_vigilant || params.include_eagleEye) {
      this.formModel.plate = params.plate;
      if (this.formModel.include_vigilant) {
        this.formModel.include_vigilant = true;
      }
      if (this.formModel.include_eagleEye) {
        this.formModel.include_eagleEye = true;
      }
    }
    this.formModel.plate = this.data?.formModel?.plate || params.plate;
    this.formModel.plate__startswith = params.plate__startswith;
    this.formModel.plate__endswith = params.plate__endswith;
    this.formModel.plate__contains__in = params.plate__contains__in;
    this.formModel.plateSearch = this.data.history ? this.data.formModel.plate : params.plateSearch;
    if (params.is_hit) {
      const isHit = params.is_hit === 'true' ? true : false;
      this.formModel.is_hit = isHit;
    }
    if (this.formModel.mode === 'Basic') {
      delete this.formModel.event_timestamp__gte;
      delete this.formModel.event_timestamp__lte;
      this.formModel = this._advancedToBasic(params);
    }

    if (params.numberOf) {
      this.formModel.numberOf = +params.numberOf;
    }

    if (params.timeValue) {
      this.formModel.timeValue = params.timeValue;
    }

    if (params.include_vigilant) {
      this.formModel.plate = params.plate;
      this.formModel.include_vigilant = params.include_vigilant === 'true' ? true : false;
    }

    if (params.include_eagleEye) {
      this.formModel.plate = params.plate;
      this.formModel.include_eagleEye = params.include_eagleEye === 'true' ? true : false;
    }

    if (params.include_flock) {
      this.formModel.include_flock = params.include_flock === 'true' ? true : false;
    }
    this._parseDynamicParams(params);
    this.form.patchValue(this.formModel);
  }

  private _genDyanmicParams(params: any): void {
    if (!this._dynamicFields.length) {
      this._dialogRef.close(params);
    } else {
      this._dynamicFields.forEach((field: any) => {
        params[field.key] = this.formModel[field.key]?.join(',');
      });
      this._dialogRef.close(params);
    }
  }

  private _parseDynamicParams(params?: any): void {
    if (!this._dynamicFields.length) {
      return;
    }
    this._dynamicFields.forEach((field: any) => {
      this.formModel[field.key] = params[field.key]?.split(',');
    });
  }

  private _genFormFields(): void {
    this._genFormOptions();
    this._genAdvancedFields();
    this._cd.markForCheck();
  }

  private _genAdvancedFields(): void {
    this.formFields = [
      {
        key: 'details__vehicleColor__in',
        type: 'select-search',
        templateOptions: {
          label: 'Vehicle Color',
          placeholder: 'Color',
          options: this.vehicleColorOptions,
        },
      },
      {
        key: 'details__vehicleMake__in',
        type: 'select-search',
        templateOptions: {
          label: 'Vehicle Make',
          placeholder: 'Make',
          options: this.vehicleMakeOptions,
        },
      },
      {
        key: 'details__vehicleModel__in',
        type: 'select-search',
        hide: this.vehicleModelOptions.length ? false : true,
        templateOptions: {
          label: 'Vehicle Model',
          placeholder: 'Model',
          options: this.vehicleModelOptions,
        },
      },
      {
        key: 'details__vehicleBodyType__in',
        type: 'select-search',
        templateOptions: {
          label: 'Vehicle Body Type',
          placeholder: 'Body Type',
          options: this.vehicleBodyTypeOptions,
        },
      },
    ];
  }

  private _genFormOptions(): void {
    this.data.fields?.vehicleColor.forEach((color) => {
      this.vehicleColorOptions.push({
        value: color,
        label: color,
      });
    });

    this.data.fields?.vehicleMake.forEach((make) => {
      this.vehicleMakeOptions.push({
        value: make,
        label: make,
      });
    });

    this.data.fields?.vehicleModel.forEach((model) => {
      this.vehicleModelOptions.push({
        value: model,
        label: model,
      });
    });

    this.data.fields?.vehicleBodyType.forEach((bodyType) => {
      this.vehicleBodyTypeOptions.push({
        value: bodyType,
        label: bodyType,
      });
    });
  }
}
